"use client";

import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAppInitialization } from "../lib/hooks/useAppInitialization";
import { useAppStore } from "../lib/stores/useAppStore";
import { useAuth } from "../lib/contexts/AuthContext";
import toast from "react-hot-toast";

// Static public paths
const PUBLIC_PATHS = ["/auth/login", "/auth/register"];

// Function to check if a path is public
const isPublicPath = (path: string) => {
  if (PUBLIC_PATHS.includes(path)) {
    return true;
  }
  if (path.startsWith("/thumbnail/")) {
    return true;
  }
  return false;
};

export function AppInitializerWithTanstack({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { user: authUser, isLoading: authLoading } = useAuth();

  // Use our custom hook that combines TanStack Query and Zustand
  const {
    userInfo,
    selectedCompany,
    isLoading: dataLoading,
  } = useAppInitialization();

  const { isInitialized, setInitialized } = useAppStore();

  // Handle authentication and redirection
  useEffect(() => {
    // Skip redirection logic for public paths
    if (isPublicPath(pathname)) {
      return;
    }

    // If auth is still loading, show loading state
    if (authLoading) {
      return;
    }

    // If auth is done loading and there's no user, redirect to login
    if (!authUser) {
      // Save the current path to redirect back after login
      if (pathname !== "/") {
        sessionStorage.setItem("redirectPath", pathname);
      }
      router.push("/auth/login");
      return;
    }

    // If user is authenticated and there's a redirect path, go there
    const redirectPath = sessionStorage.getItem("redirectPath");
    if (redirectPath) {
      sessionStorage.removeItem("redirectPath");
      router.push(redirectPath);
    }
  }, [authUser, authLoading, router, pathname]);

  // Mark as initialized when data is loaded
  useEffect(() => {
    if (!authLoading && !dataLoading && userInfo) {
      setInitialized(true);
    }
  }, [authLoading, dataLoading, userInfo, setInitialized]);

  // Show company notification
  useEffect(() => {
    if (selectedCompany && isInitialized) {
      toast.success(`Connected to company`, {
        duration: 3000,
      });
    }
  }, [selectedCompany?.company_id, isInitialized, selectedCompany]);

  // Show loading state only for protected routes while auth is initializing
  // if ((authLoading || dataLoading) && !isPublicPath(pathname)) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen">
  //       <div className="text-center">
  //         <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
  //         <p className="mt-2 text-gray-600">
  //           {authLoading
  //             ? "Initializing authentication..."
  //             : "Loading application data..."}
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  return <>{children}</>;
}
